<template>
  <NoRecord v-if="isEmpty" />
  <Chart v-else :options="chartData" />
</template>

<script>
import { Chart } from "highcharts-vue";
import moment from "jalali-moment";
import { mapGetters } from "vuex";
import NoRecord from "@/components/lists/NoRecord.vue";
import { RECOMMENDATION_RANGES } from "@/core/constants";

export default {
  name: "HMIChart",

  computed: {
    ...mapGetters(["HMIChartData", "HMIChartConfig"]),
    isEmpty() {
      return Object.values(this.HMIChartData).every(
        (data) => data.length === 0
      );
    },
    unit() {
      return this.HMIChartConfig.parameter === "production_rate" ? "t/h" : "%";
    },
    minDate() {
      return new Date(this.HMIChartConfig.from).getTime();
    },
    maxDate() {
      return new Date(this.HMIChartConfig.to).getTime();
    },
    yAxisTickInterval() {
      switch (this.HMIChartConfig.parameter) {
        case "production_rate":
          return 10;
        case "metallization":
          return 1;
        case "carbon":
          return 0.1;
        default:
          return 10;
      }
    },
    minParam() {
      const key =
        this.HMIChartConfig.parameter === "production_rate"
          ? "productionRate"
          : this.HMIChartConfig.parameter;
      return RECOMMENDATION_RANGES[key].min - this.yAxisTickInterval;
    },
    maxParam() {
      const key =
        this.HMIChartConfig.parameter === "production_rate"
          ? "productionRate"
          : this.HMIChartConfig.parameter;
      return RECOMMENDATION_RANGES[key].max + this.yAxisTickInterval;
    },
    chartData: function () {
      return {
        chart: {
          spacingTop: 20,
          height: 265,
          marginBottom: 110,
        },
        title: {
          text: "",
        },
        xAxis: {
          type: "datetime",
          gridLineWidth: 1,
          gridLineDashStyle: "dash",
          lineColor: "var(--v-lightGrey-base)",
          lineWidth: 2,
          minPadding: 5,
          maxPadding: 5,
          className: "chart-axis",
          min: this.minDate,
          max: this.maxDate,
          tickPositioner: function () {
            const ticks = [];
            const interval = Math.floor((this.max - this.min) / 10);
            for (let i = this.min; i <= this.max; i += interval) {
              ticks.push(i);
            }
            return ticks;
          },
          labels: {
            rotation: -60,
            align: "center",
            y: 40,
            x: -10,
            formatter: function () {
              return `${moment(new Date(this.value)).format(
                "jYYYY/jMM/jDD"
              )}</br>${moment(new Date(this.value)).format("HH:mm")}`;
            },
            style: {
              fontSize: "10px",
              whiteSpace: "nowrap",
            },
          },
        },
        plotOptions: {
          series: {
            states: {
              inactive: {
                opacity: 1,
              },
              hover: {
                enabled: true,
              },
            },
            marker: {
              enabled: true,
              states: {
                hover: {
                  enabled: true,
                },
              },
            },
          },
        },
        yAxis: {
          gridLineWidth: 0.7,
          lineColor: "var(--v-lightGrey-base)",
          lineWidth: 2,
          title: {
            enabled: false,
          },
          className: "chart-axis",
          min: this.minParam,
          max: this.maxParam,
          tickInterval: this.yAxisTickInterval,
          // gridLineDashStyle: "dash",
        },
        tooltip: {
          backgroundColor: "var(--v-background-base)",
          borderColor: "transparent",
          borderRadius: 8,
          shape: "rect",
          className: "chart-tooltip",
          shadow: false,
          shared: false,
          crosshairs: false,
          formatter: function () {
            const jalaliDate = moment(new Date(this.x)).format(
              "jYYYY/jMM/jDD hh:mm"
            );
            let tooltipText = `<span>${jalaliDate}</span><br/>`;
            tooltipText += `<span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${this.y} ${this.series.options.unit}</b><br/>`;
            return tooltipText;
          },
        },
        legend: {
          enabled: true,
        },
        credits: {
          enabled: false,
        },
        series: [
          {
            connectNulls: true,
            name: "Lab Data",
            type: "line",
            marker: {
              radius: 3,
              fillColor: "var(--v-infoGreen-base)",
              symbol: "circle",
            },
            lineWidth: 1.5,
            color: "var(--v-infoGreen-lighten2)",
            data: this.HMIChartData.lab,
            unit: this.unit,
          },
          {
            connectNulls: true,
            name: "3 hours",
            type: "line",
            marker: {
              radius: 3,
              fillColor: "black",
              symbol: "circle",
            },
            lineWidth: 1.5,
            color: "grey",
            data: this.HMIChartData[3],
            unit: this.unit,
          },
          {
            connectNulls: true,
            name: "5 hours",
            type: "line",
            marker: {
              radius: 3,
              fillColor: "var(--v-mainBlue-base)",
              symbol: "circle",
            },
            lineWidth: 1.5,
            color: "var(--v-mainBlue-lighten2)",
            data: this.HMIChartData[5],
            unit: this.unit,
          },
          {
            connectNulls: true,
            name: "8 hours",
            type: "line",
            marker: {
              radius: 3,
              fillColor: "var(--v-mainRed-base)",
              symbol: "circle",
            },
            lineWidth: 1.5,
            color: "var(--v-mainRed-lighten2)",
            data: this.HMIChartData[8],
            unit: this.unit,
          },
        ],
      };
    },
  },

  components: {
    Chart,
    NoRecord,
  },
};
</script>

<style>
.highcharts-title {
  font-family: "Vazirmatn" !important;
}
.chart-tooltip {
  text-align: right !important;
}
.tootltip-unit {
  font-size: 10px;
  margin-left: 4px;
}
.chart-tooltip > span {
  font-family: "Vazirmatn" !important;
}
.chart-axis {
  font-family: "Vazirmatn" !important;
}
</style>
